// ---------------------------------------------------------------------------
// C11-C13: Notification Center — Slide Panel + Smart Bundling + Digest
// ---------------------------------------------------------------------------
// Slide from right on bell click (300ms). Grouped: Now / Today / Yesterday.
// Priority-colored dots. Smart bundling (3+ related → single insight).
// Morning digest (9am overnight summary).
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { formatDate } from "@/lib/i18n";
import { Skeleton } from "@/components/skeleton";
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Users,
  Mail,
  CreditCard,
  Calendar,
  Zap,
  FileText,
  Settings,
  Phone,
  Sparkles,
  Newspaper,
  AlertCircle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NotificationModule = "crm" | "email" | "payments" | "bookings" | "workflows" | "pages" | "system";

type NotificationPriority = "low" | "normal" | "high" | "urgent";

interface Notification {
  id: string;
  module: NotificationModule;
  title: string;
  message: string;
  linkTo: string | null;
  isRead: boolean;
  createdAt: string;
  priority: NotificationPriority;
  contactId?: string;
  contactName?: string;
}

interface BundledNotification {
  type: "single";
  notification: Notification;
}

interface BundledGroup {
  type: "bundle";
  contactName: string;
  contactId: string;
  count: number;
  notifications: Notification[];
  summary: string;
  action?: { label: string; href: string };
}

type DisplayItem = BundledNotification | BundledGroup;

interface MorningDigestData {
  generatedAt: string;
  windowHours: number;
  emailsSent: number;
  opens: number;
  replies: number;
  newContacts: number;
  dealsWon: number;
  tasksDue: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODULE_ICONS: Record<NotificationModule, typeof Users> = {
  crm: Users,
  email: Mail,
  payments: CreditCard,
  bookings: Calendar,
  workflows: Zap,
  pages: FileText,
  system: Settings,
};

const PRIORITY_DOT: Record<NotificationPriority, string> = {
  low: "bg-[var(--color-muted-foreground)]",
  normal: "bg-[var(--color-primary)]",
  high: "bg-[var(--color-warning)]/50",
  urgent: "bg-[var(--color-destructive)]/50",
};

// ---------------------------------------------------------------------------
// Time grouping
// ---------------------------------------------------------------------------

type TimeGroup = "now" | "today" | "yesterday" | "older";

function getTimeGroup(dateStr: string): TimeGroup {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = diffMs / 60_000;

  if (diffMin < 60) return "now";

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86_400_000);

  if (date >= todayStart) return "today";
  if (date >= yesterdayStart) return "yesterday";
  return "older";
}

const TIME_GROUP_LABELS: Record<TimeGroup, string> = {
  now: "Now",
  today: "Earlier Today",
  yesterday: "Yesterday",
  older: "Older",
};

// ---------------------------------------------------------------------------
// Smart bundling (C12)
// ---------------------------------------------------------------------------

function bundleNotifications(notifications: Notification[]): DisplayItem[] {
  // Group by contactId
  const byContact = new Map<string, Notification[]>();
  const noContact: Notification[] = [];

  for (const n of notifications) {
    if (n.contactId && n.contactName) {
      const key = n.contactId;
      const list = byContact.get(key);
      if (list) list.push(n);
      else byContact.set(key, [n]);
    } else {
      noContact.push(n);
    }
  }

  const items: DisplayItem[] = [];

  // Bundle contacts with 3+ notifications
  for (const [contactId, group] of byContact) {
    if (group.length >= 3) {
      const contactName = group[0]!.contactName!;
      items.push({
        type: "bundle",
        contactName,
        contactId,
        count: group.length,
        notifications: group,
        summary: `${contactName}: ${group.length} actions in the last hour — they're engaged.`,
        action: { label: "View Contact", href: `/crm/contacts/${contactId}` },
      });
    } else {
      for (const n of group) {
        items.push({ type: "single", notification: n });
      }
    }
  }

  for (const n of noContact) {
    items.push({ type: "single", notification: n });
  }

  // Sort by most recent
  return items.sort((a, b) => {
    const dateA = a.type === "single" ? a.notification.createdAt : a.notifications[0]!.createdAt;
    const dateB = b.type === "single" ? b.notification.createdAt : b.notifications[0]!.createdAt;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatTimeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return formatDate(dateStr);
}

// ---------------------------------------------------------------------------
// Morning Digest Check (C13)
// ---------------------------------------------------------------------------

const DIGEST_KEY = "ease:morning-digest-shown";

function shouldShowMorningDigest(): boolean {
  const now = new Date();
  const hour = now.getHours();
  if (hour < 7 || hour > 11) return false; // Only show 7am-11am

  const today = now.toISOString().split("T")[0];
  try {
    const lastShown = localStorage.getItem(DIGEST_KEY);
    if (lastShown === today) return false;
  } catch { /* storage error */ }

  return true;
}

function markDigestShown() {
  const today = new Date().toISOString().split("T")[0]!;
  try {
    localStorage.setItem(DIGEST_KEY, today);
  } catch { /* storage full */ }
}

// ---------------------------------------------------------------------------
// Notification Item
// ---------------------------------------------------------------------------

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
}: {
  notification: Notification;
  onClick: () => void;
  onMarkRead: () => void;
}) {
  const Icon = MODULE_ICONS[notification.module] ?? Settings;

  return (
    <div
      onClick={onClick}
      className={`stagger-item flex cursor-pointer gap-3 px-4 py-3 hover:bg-muted/50 transition-colors ${
        !notification.isRead ? "bg-[var(--color-primary)]/5" : ""
      }`}
    >
      <div className="relative mt-0.5 flex-shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        {!notification.isRead && (
          <span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ${PRIORITY_DOT[notification.priority] ?? PRIORITY_DOT.normal}`} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${!notification.isRead ? "font-medium text-foreground" : "text-muted-foreground"}`}>
          {notification.title}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{notification.message}</p>
        <p className="mt-1 text-[10px] text-muted-foreground">{formatTimeAgo(notification.createdAt)}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bundle Item
// ---------------------------------------------------------------------------

function BundleItem({ bundle, onNavigate }: { bundle: BundledGroup; onNavigate: (href: string) => void }) {
  return (
    <div className="stagger-item px-4 py-3 bg-[var(--color-ai)]/5 border-l-2 border-[var(--color-ai)]">
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-[var(--color-ai)] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{bundle.summary}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {bundle.count} notifications bundled
          </p>
          {bundle.action && (
            <button
              onClick={() => onNavigate(bundle.action!.href)}
              className="mt-2 inline-flex items-center gap-1 rounded-lg bg-[var(--color-ai)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-ai)]/90 transition-colors"
            >
              <Phone className="h-3 w-3" />
              {bundle.action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestError, setDigestError] = useState(false);
  const [digest, setDigest] = useState<MorningDigestData | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Fetch notifications
  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ notifications: Notification[] }>("/api/notifications?limit=50");
      setNotifications(data.notifications);
    } catch {
      // Silent fail — notifications are non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount + poll every 30s
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30_000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // C13: Morning digest check
  useEffect(() => {
    if (shouldShowMorningDigest()) {
      setShowDigest(true);
      markDigestShown();
    }
  }, []);

  useEffect(() => {
    if (!showDigest) {
      return;
    }

    let cancelled = false;
    setDigestLoading(true);
    setDigestError(false);

    apiFetch<{ data: MorningDigestData }>("/v1/analytics/digest")
      .then((response) => {
        if (cancelled) return;
        setDigest(response.data);
      })
      .catch(() => {
        if (cancelled) return;
        setDigest(null);
        setDigestError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setDigestLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showDigest]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Mark as read
  const markAsRead = useCallback(async (id: string) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: "POST" });
    } catch { /* optimistic update already applied */ }
  }, []);

  // Mark all read
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      await apiFetch("/api/notifications/read-all", { method: "POST" });
    } catch { /* optimistic update already applied */ }
  }, []);

  // Handle notification click
  const handleClick = useCallback((notification: Notification) => {
    markAsRead(notification.id);
    if (notification.linkTo) {
      navigate(notification.linkTo);
      setIsOpen(false);
    }
  }, [markAsRead, navigate]);

  // C12: Smart bundling + time grouping
  const bundledItems = useMemo(() => bundleNotifications(notifications), [notifications]);

  const groupedByTime = useMemo(() => {
    const groups = new Map<TimeGroup, DisplayItem[]>();
    for (const item of bundledItems) {
      const date = item.type === "single" ? item.notification.createdAt : item.notifications[0]!.createdAt;
      const group = getTimeGroup(date);
      const list = groups.get(group);
      if (list) list.push(item);
      else groups.set(group, [item]);
    }
    return groups;
  }, [bundledItems]);

  const digestItems = useMemo(() => {
    if (!digest) return [];
    return [
      { label: "Emails sent", value: digest.emailsSent },
      { label: "Opens", value: digest.opens },
      { label: "Replies", value: digest.replies },
      { label: "New contacts", value: digest.newContacts },
      { label: "Deals won", value: digest.dealsWon },
      { label: "Tasks due", value: digest.tasksDue },
    ];
  }, [digest]);

  return (
    <>
      {/* Bell Button (in topbar) */}
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) loadNotifications(); }}
        className="relative rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-destructive)]/50 text-[10px] font-bold text-white animate-in zoom-in duration-200">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Slide-from-right Panel (Portal) */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div
            ref={panelRef}
            className="fixed right-0 top-0 z-50 h-full w-96 bg-background border-l border-border shadow-xl animate-in slide-in-from-right duration-300"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                  >
                    <CheckCheck className="h-3 w-3" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* C13: Morning Digest (if applicable) */}
            {showDigest && (
              <div className="border-b border-border bg-[var(--color-primary)]/5 px-4 py-3">
                <div className="flex items-start gap-2">
                  <Newspaper className="h-4 w-4 text-[var(--color-primary)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Good morning!</p>
                    {digestLoading ? (
                      <div className="mt-2 space-y-2">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <Skeleton key={index} className="h-3 w-40" />
                        ))}
                      </div>
                    ) : digestError ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">No data available</p>
                    ) : digest ? (
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {digestItems.map((item) => (
                          <li key={item.label} className="flex items-center justify-between gap-4">
                            <span>{item.label}</span>
                            <span className="font-medium text-foreground tabular-nums">{item.value}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-0.5 text-xs text-muted-foreground">No data available</p>
                    )}
                    <button
                      onClick={() => { navigate("/briefing"); setIsOpen(false); }}
                      className="mt-2 text-xs text-[var(--color-primary)] hover:underline"
                    >
                      View full briefing →
                    </button>
                  </div>
                  <button
                    onClick={() => setShowDigest(false)}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto" style={{ height: "calc(100% - 56px)" }}>
              {loading && notifications.length === 0 ? (
                <div className="space-y-3 px-4 py-6">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="flex gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Bell className="h-8 w-8 text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No notifications yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Activity from your contacts, campaigns, and workflows will appear here.
                  </p>
                </div>
              ) : (
                Array.from(groupedByTime.entries()).map(([group, items]) => (
                  <div key={group}>
                    <div className="sticky top-0 bg-background/95 backdrop-blur-sm px-4 py-2 text-xs font-medium text-muted-foreground border-b border-border/50">
                      {TIME_GROUP_LABELS[group]}
                    </div>
                    {items.map((item, idx) => (
                      item.type === "single" ? (
                        <NotificationItem
                          key={item.notification.id}
                          notification={item.notification}
                          onClick={() => handleClick(item.notification)}
                          onMarkRead={() => markAsRead(item.notification.id)}
                        />
                      ) : (
                        <BundleItem
                          key={`bundle-${item.contactId}`}
                          bundle={item}
                          onNavigate={(href) => { navigate(href); setIsOpen(false); }}
                        />
                      )
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
